<template>
  <PageView
    class="with-submenu"
    :application="application"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application.localName"
      role="navigation"
    />
    <h1 class="title main-title">
      {{ $t("titles.authorization-application-management", { name: application.localName }) }}
    </h1>
    <div class="contentView">
      <LoadingAnimate
        v-if="isLoading"
        :size="'is-large'"
      />
      <b-table
        v-else
        :data="listUsersAuth"
        class="row tableAuth"
        :paginated="listUsersAuth.length > 20"
        :total="listUsersAuth.length"
        :aria-current-label="$t('menu.aria-curent-page')"
        :aria-label="$t('menu.aria-pagination')"
        :aria-next-label="$t('menu.aria-next-page')"
        :aria-previous-label="$t('menu.aria-previous-page')"
      >
        <b-table-column
          :custom-search="searchByLogin"
          :label="$t('dataTypeAuthorizations.users')"
          :aria-label="$t('dataTypeAuthorizations.users')"
          field="label"
          searchable
          sortable
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('dataTypeAuthorizations.search')"
              :aria-label="$t('dataTypeAuthorizations.search')"
              icon="search"
              size="is-normal"
            />
          </template>
          <template #default="props">
            <span :aria-label="props.row.label" class="user-label-cell">
              <span :class="{ 'user-is-already-manager': isManager(props.row) }">
                {{ props.row.label }}
              </span>
              <span
                v-if="isManager(props.row)"
                class="manager-badge"
                :title="$t('dataTypeAuthorizations.manager-badge-tooltip')"
              >
                ★
              </span>
            </span>
          </template>
        </b-table-column>
        <b-table-column
          v-if="currentUser.applicationManager || currentUser.authorizedForApplicationCreation"
          v-slot="props"
          :label="$t('dataTypeAuthorizations.status-auth-application-manager')"
          :aria-label="$t('dataTypeAuthorizations.status-auth-application-manager')"
          field="applicationManager"
          sortable
        >
          <span :title="applicationManagerTooltip(props.row)">
            <b-checkbox
              v-model="props.row.isApplicationManager"
              :aria-label="$t('dataTypeAuthorizations.status-auth-application-manager')+': '+props.row.isApplicationManager"
              :disabled="isApplicationManagerDisabled(props.row)"
              @update:model-value="onToggle($event, props.row, 'applicationManager')"
            />
          </span>
        </b-table-column>
        <b-table-column
          v-if="currentUser.userManager || currentUser.authorizedForApplicationCreation"
          v-slot="props"
          :label="$t('dataTypeAuthorizations.status-auth-user-manager')"
          :aria-label="$t('dataTypeAuthorizations.status-auth-user-manager')"
          field="userManager"
          sortable
        >
          <span :title="userManagerTooltip(props.row)">
            <b-checkbox
              v-model="props.row.isUserManager"
              :aria-label="$t('dataTypeAuthorizations.status-auth-user-manager')+': '+props.row.isUserManager"
              :disabled="isUserManagerDisabled(props.row)"
              @update:model-value="onToggle($event, props.row, 'userManager')"
            />
          </span>
        </b-table-column>
      </b-table>
      <div class="row">
        <div class="column is-offset-10 is-2">
          <b-button
            icon-left="floppy-disk"
            icon-pack="far"
            expanded
            rounded
            type="is-dark is-right"
            @click="registerChanges"
          />
        </div>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
import SubMenu from "@/components/common/SubMenu.vue";
import PageView from "../../common/PageView.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import { onMounted, ref } from "vue";
import useArray from "@/composable/components/array";
import useObject from "@/composable/components/object";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import services from "@/composable/services";
import { BButton, BCheckbox, BInput, BTable, BTableColumn } from "buefy";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { useRoleManagementChanges } from "@/composable/authorization/useRoleManagementChanges";
import type { UserRow } from "@/composable/authorization/roleManagementChangesCore";

export default {
  name: "AuthorizationsManagementForApplicationUsersView",
  components: {
    BTable,
    BInput,
    BTableColumn,
    BCheckbox,
    BButton,
    LoadingAnimate,
    PageView,
    SubMenu,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const { shallowRefArray: listUsersAuth, doChangeArray: changeListUsersAuth } = useArray();
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const isLoading = ref(false);
    const currentUser = services.loginService.getAuthenticatedUser();

    const {
      resetChanges,
      onToggle,
      persistChanges,
      isApplicationManagerDisabled,
      isUserManagerDisabled,
      applicationManagerTooltip,
      userManagerTooltip,
    } = useRoleManagementChanges({
      applicationName: props.applicationName,
      currentUser,
    });

    onMounted(async () => {
      await init();
      changeSubMenuPaths([
        new SubMenuPath(
          t("titles.authorization-application-management", { name: application.localName })
            .toLowerCase(),
          () => router.push(`/applications/${props.applicationName}/authorizationsManagementForApplicationUsers`),
          () => router.push(`/applications/${props.applicationName}`),
        ),
      ]);
    });

    function searchByLogin(user: { login?: string }, term: string): boolean {
      return !!user.login?.match(term);
    }

    function isManager(row: UserRow): boolean {
      return !!row?.isApplicationManager || !!row?.isUserManager;
    }

    async function init(): Promise<void> {
      isLoading.value = true;
      try {
        resetChanges();
        const listUsersApp = await services.authorizationService.getAuthorizationGrantableInfos(props.applicationName);
        changeListUsersAuth(listUsersApp.users);
        const fetchedApplication = await services.applicationService.getApplication(props.applicationName, [
          "CONFIGURATION",
          "DATATYPE",
          "REFERENCETYPE",
        ]);
        if (fetchedApplication) changeApplication(fetchedApplication);
      }
      finally {
        isLoading.value = false;
      }
    }

    async function registerChanges(): Promise<void> {
      try {
        await persistChanges();
      }
      finally {
        // Recharge depuis le backend - meme si une partie des appels a
        // echoue ( ex auto-revocation HTTP 400 ) , l'UI doit converger
        // vers l'etat reel persiste pour eviter une divergence entre
        // les checkboxes locales et la BDD .
        changeListUsersAuth([]);
        await init();
      }
    }

    return {
      subMenuPaths,
      application,
      listUsersAuth,
      isLoading,
      currentUser,
      searchByLogin,
      isManager,
      onToggle,
      registerChanges,
      isApplicationManagerDisabled,
      isUserManagerDisabled,
      applicationManagerTooltip,
      userManagerTooltip,
    };
  },
};
</script>

<style scoped>
.user-label-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.user-is-already-manager {
  font-style: italic;
  color: #6b7280;
}

.manager-badge {
  color: #d4a017;
  font-weight: 700;
}
</style>
